import { useState, type FormEvent } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db7mfcv3hiaolq192ja0";

const contactInfo = [
  { icon: "ri-mail-line", label: "邮箱", value: "hello@anime-site.com" },
  { icon: "ri-phone-line", label: "客服热线", value: "400-888-2024" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "上海市浦东新区动漫产业园 A 座 12 层" },
  { icon: "ri-time-line", label: "服务时间", value: "周一至周五 10:00 - 18:00" },
];

const socials = [
  { icon: "ri-weibo-fill", label: "微博", href: "https://51heigua2.asia" },
  { icon: "ri-bilibili-fill", label: "哔哩哔哩", href: "https://www.bilibili.com" },
  { icon: "ri-youtube-fill", label: "视频频道", href: "https://www.youtube.com" },
  { icon: "ri-wechat-fill", label: "微信公众号", href: "https://weixin.qq.com" },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useDocumentTitle(
    "联系我们 - 动漫网站",
    "联系动漫网站团队，提交你的反馈与咨询，我们会尽快回复。查看官方邮箱、社交媒体与办公地址。"
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === "website_alt") return;
        const text = String(value).trim();
        if (text) params.append(key, text);
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code ?? "";
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试");
    }
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb items={[{ label: "联系我们" }]} />

      <header className="mt-5 mb-8">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">联系我们</h1>
        <p className="text-sm text-foreground-600 mt-2 max-w-2xl">
          有任何建议、合作意向或使用问题，欢迎随时联系我们。我们会认真阅读每一条反馈。
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-8 lg:gap-12">
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {contactInfo.map((item) => (
              <div key={item.label} className="flex items-start gap-3.5 p-5 rounded-lg bg-background-50 border border-background-200/70">
                <span className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-primary-600 text-xl`} />
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-400">{item.label}</p>
                  <p className="text-sm text-foreground-900 mt-1 break-words">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <h2 className="font-heading font-semibold text-foreground-950 mb-4">关注我们</h2>
            <div className="flex flex-wrap gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 h-11 px-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  <i className={`${s.icon} text-lg`} />
                  {s.label}
                </a>
              ))}
            </div>
          </div>

          <div className="mt-8 p-5 rounded-lg bg-secondary-50 border border-secondary-200/70">
            <h3 className="font-heading font-semibold text-foreground-900 flex items-center gap-2">
              <i className="ri-question-line text-accent-600" />
              常见问题
            </h3>
            <ul className="mt-3 space-y-2.5 text-sm text-foreground-600">
              <li>· 收藏的动漫会保存在浏览器本地，更换设备后需要重新收藏。</li>
              <li>· 本站资源与资讯均为演示内容，播放功能待接入真实接口。</li>
              <li>· 如需内容合作或版权相关事宜，请通过上方邮箱联系编辑部。</li>
            </ul>
          </div>
        </div>

        <div className="lg:sticky lg:top-24 h-fit">
          <div className="p-6 rounded-lg bg-background-50 border border-background-200/70">
            <h2 className="font-heading font-bold text-lg text-foreground-950">发送反馈</h2>
            <p className="text-sm text-foreground-500 mt-1.5">填写下方表单，我们会尽快与你联系。</p>

            {status === "success" ? (
              <div className="mt-6 py-10 text-center">
                <span className="w-14 h-14 mx-auto rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-2xl text-primary-600" />
                </span>
                <h3 className="font-heading font-semibold text-foreground-950 mt-4">提交成功</h3>
                <p className="text-sm text-foreground-500 mt-2">感谢你的反馈，我们已收到你的留言。</p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-5 h-10 px-5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 cursor-pointer"
                >
                  再写一条
                </button>
              </div>
            ) : (
              <form
                id="contact-feedback-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-5 space-y-4"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的姓名"
                    className="w-full h-11 px-3.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full h-11 px-3.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                    留言
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请输入你的反馈或咨询内容（不超过 500 字）"
                    className="w-full p-3.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
                  />
                </div>

                <input
                  className="contact-form-verify"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <p className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3.5 py-2.5">
                    <i className="ri-error-warning-line mr-1.5" />
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交反馈
                    </>
                  )}
                </button>
                <p className="text-xs text-foreground-400 text-center">
                  提交即表示你同意我们使用该信息与你联系
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}